/**
 * "Entrar como usuário" (impersonate do suporte), no cabeçalho do modal de Detalhes.
 *
 * Pede motivo/ticket, abre a sessão na API e a aplicação do cliente numa NOVA
 * aba (/impersonate#imp=...). A aba do admin continua logada como admin.
 * Sessão curta e auditada; senha, e-mail e cobrança ficam bloqueados.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { LogIn, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  endAdminImpersonation,
  getActiveImpersonation,
  startImpersonation,
  type AdminImpersonationSession,
  type AdminUserDetail,
} from '@/services/adminService';
import { buildImpersonationUrl } from '@/lib/impersonation';
import { formatDate, type useFeedback } from './shared';

interface ImpersonateActionProps {
  detail: AdminUserDetail;
  feedback: ReturnType<typeof useFeedback>;
  onChanged: () => void;
}

const ImpersonateAction: React.FC<ImpersonateActionProps> = ({ detail, feedback, onChanged }) => {
  const userId = detail.auth.id;
  const isAdminTarget = detail.profile?.role === 'admin';
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState<AdminImpersonationSession | null>(null);

  const loadActive = useCallback(async () => {
    try {
      setActive((await getActiveImpersonation()).session);
    } catch {
      setActive(null);
    }
  }, []);

  useEffect(() => {
    loadActive();
  }, [loadActive]);

  const handleStart = async () => {
    // Abre a aba ainda no clique (senão o bloqueador de pop-up barra) e navega depois.
    const tab = window.open('about:blank', '_blank');
    setBusy(true);
    feedback.clear();
    try {
      const result = await startImpersonation({ userId, reason: reason.trim() });
      const url = buildImpersonationUrl(window.location.origin, result);
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
      feedback.success(
        `Sessão de suporte aberta em outra aba como ${result.target.email ?? 'usuário'}, até ${formatDate(result.expiresAt)}.`,
      );
      setOpen(false);
      setReason('');
      await loadActive();
      onChanged();
    } catch (err) {
      tab?.close();
      feedback.error(err);
    } finally {
      setBusy(false);
    }
  };

  const handleEnd = async () => {
    if (!active) return;
    setBusy(true);
    feedback.clear();
    try {
      await endAdminImpersonation(active.id);
      feedback.success('Sessão de suporte encerrada.');
      await loadActive();
      onChanged();
    } catch (err) {
      feedback.error(err);
    } finally {
      setBusy(false);
    }
  };

  const activeHere = active?.targetUserId === userId;

  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen((value) => !value)}
          disabled={busy || isAdminTarget}
          title={isAdminTarget ? 'Não é permitido entrar como outro admin.' : undefined}
        >
          <LogIn className="h-4 w-4" /> Entrar como usuário
        </Button>
        {active && (
          <>
            <span className="text-xs text-muted-foreground">
              {activeHere ? 'Sessão de suporte ativa neste usuário' : 'Você tem uma sessão de suporte ativa em outro usuário'}
              {' '}até {formatDate(active.expiresAt)}.
            </span>
            <Button size="sm" variant="ghost" onClick={handleEnd} disabled={busy}>
              <LogOut className="h-4 w-4" /> Encerrar
            </Button>
          </>
        )}
      </div>

      {open && (
        <div className="rounded-lg border border-border p-3 space-y-2">
          <div className="space-y-1">
            <Label htmlFor="impersonate-reason" className="text-xs text-muted-foreground">Motivo / ticket (obrigatório)</Label>
            <Input
              id="impersonate-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: Ticket #1234 — erro ao gerar carrossel"
              maxLength={500}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Abre o app como {detail.auth.email} em uma nova aba, por até 30 minutos. Tudo fica registrado na Auditoria.
            Troca de senha, e-mail, perfil e cobrança ficam bloqueados. Gerações feitas aqui não contam na cota do usuário,
            mas projetos criados ficam na conta dele.
            {active && ' A sessão de suporte atual será encerrada.'}
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleStart} disabled={busy || reason.trim().length < 3}>
              {busy ? 'Abrindo…' : 'Abrir sessão de suporte'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpersonateAction;
